// ────────────────────────────────────────────────────────────────────────────
//  Server.tsx — Marketing-Landing für CustoSoft Server (On-Premise-Edition)
//
//  Animierte Verkaufsseite — keine Daten aus dem Backend, alles statisch.
//  Erreichbar über /server. Hauptkontakt: server@custosoft.de
//
//  Sektionen:
//   1. Hero        — animierter Mesh-BG + Typewriter + Doppel-CTA
//   2. Trust Bar   — Logos / Versprechen marquee
//   3. Features    — 9-Karten-Grid
//   4. Deployment  — 5 Auslieferungsformate animiert
//   5. Process     — 5-Schritt-Timeline
//   6. Pricing     — 4 Tier-Karten + Anfrage-CTA
//   7. Update-Abo  — INKL/EXKL-Karten
//   8. Beta-Tester — Beta-Programm + Modal-CTA
//   9. FAQ         — Akkordeon
//  10. Final CTA   — Beta-Anmeldung + E-Mail
//  11. Footer
//
//  KEIN Cloud-vs-Server-Vergleich auf dieser Seite — wir hosten keine
//  Privat-/Firmensysteme, nur die SaaS-Variante für individuelle App-User
//  (auf der separaten Landing). Diese Seite ist nur über self-hosting.
// ────────────────────────────────────────────────────────────────────────────

import { useEffect, useRef, useState } from 'react'
import { Link } from 'react-router-dom'
import { api } from '../lib/api'

const CONTACT_EMAIL = 'server@custosoft.de'
const CONTACT_FALLBACK = 'taironic.media@gmail.com'

export function Server() {
  const [betaOpen, setBetaOpen] = useState(false)

  useEffect(() => {
    const prev = document.title
    document.title = 'CustoSoft Server · On-Premise HR-Suite (in Planung)'
    return () => { document.title = prev }
  }, [])

  // IntersectionObserver für reveal-on-scroll
  useEffect(() => {
    const io = new IntersectionObserver((entries) => {
      for (const e of entries) {
        if (e.isIntersecting) {
          e.target.classList.add('in-view')
          io.unobserve(e.target)
        }
      }
    }, { threshold: 0.12 })
    document.querySelectorAll('.reveal').forEach(el => io.observe(el))
    return () => io.disconnect()
  }, [])

  return (
    <div className="relative min-h-full overflow-hidden" style={{ background: 'var(--bg)' }}>
      <PlanningBanner />
      <ServerNav onOpenBeta={() => setBetaOpen(true)} />
      <Hero onOpenBeta={() => setBetaOpen(true)} />
      <TrustBar />
      <Features />
      <Deployment />
      <Process />
      <Pricing />
      <UpdateAbo />
      <BetaTester onOpenBeta={() => setBetaOpen(true)} />
      <FAQ />
      <FinalCTA onOpenBeta={() => setBetaOpen(true)} />
      <ServerFooter />
      {betaOpen && <BetaModal onClose={() => setBetaOpen(false)} />}
    </div>
  )
}

// ════════════════════════════════════════════════════════════════════════════
// 0. PLANNING BANNER — sticky oben auf der Seite
// ════════════════════════════════════════════════════════════════════════════
function PlanningBanner() {
  return (
    <div
      className="relative z-40 px-4 py-2.5 text-center text-xs md:text-sm font-semibold"
      style={{
        background: 'linear-gradient(90deg, rgba(255,170,61,0.20), rgba(242,64,76,0.20), rgba(138,68,238,0.20))',
        borderBottom: '1px solid rgba(255,170,61,0.35)',
        color: '#ffaa3d',
        letterSpacing: '0.02em',
      }}
    >
      🛠 <strong className="uppercase tracking-wider">In Planung</strong>
      <span className="hidden md:inline text-app-text-dim font-normal mx-2">·</span>
      <span className="text-app-text-dim font-normal">
        CustoSoft Server befindet sich aktuell in der Konzeptphase. Werden Sie Beta-Tester und sichern Sie sich
      </span>
      <strong className="text-white mx-1">20 % Rabatt</strong>
      <span className="text-app-text-dim font-normal">auf den Endpreis.</span>
    </div>
  )
}

// ════════════════════════════════════════════════════════════════════════════
// 1. NAV
// ════════════════════════════════════════════════════════════════════════════
function ServerNav({ onOpenBeta }: { onOpenBeta: () => void }) {
  return (
    <nav className="relative z-30 px-6 py-5 flex items-center justify-between">
      <Link to="/" className="text-sm font-bold tracking-tight">CustoSoft</Link>
      <div className="flex items-center gap-4 text-sm">
        <a href="#features"  className="hidden md:inline text-app-text-dim hover:text-app-text">Features</a>
        <a href="#pricing"   className="hidden md:inline text-app-text-dim hover:text-app-text">Pricing</a>
        <a href="#process"   className="hidden md:inline text-app-text-dim hover:text-app-text">Ablauf</a>
        <a href="#updates"   className="hidden md:inline text-app-text-dim hover:text-app-text">Updates</a>
        <a href="#beta"      className="hidden md:inline text-app-text-dim hover:text-app-text">🧪 Beta</a>
        <a href="#faq"       className="hidden md:inline text-app-text-dim hover:text-app-text">FAQ</a>
        <Link to="/" className="text-xs text-app-text-mute hover:text-app-text">← Zurück zur Hauptseite</Link>
        <button
          type="button"
          onClick={onOpenBeta}
          className="text-sm font-semibold px-4 py-2 rounded-full text-white"
          style={{ background: 'linear-gradient(135deg, #8a44ee, #3a62ff)' }}
        >
          Beta-Tester werden
        </button>
      </div>
    </nav>
  )
}

// ════════════════════════════════════════════════════════════════════════════
// 2. HERO mit Typewriter
// ════════════════════════════════════════════════════════════════════════════
function Hero({ onOpenBeta }: { onOpenBeta: () => void }) {
  const ROTATE = ['Ihrem Server', 'Ihrem Büro', 'Ihrem Netz', 'Ihrer Hand']
  const [idx, setIdx] = useState(0)
  const [shown, setShown] = useState('')
  const [phase, setPhase] = useState<'typing' | 'pause' | 'deleting'>('typing')

  useEffect(() => {
    const target = ROTATE[idx]
    let timeout: ReturnType<typeof setTimeout>
    if (phase === 'typing') {
      if (shown.length < target.length) {
        timeout = setTimeout(() => setShown(target.slice(0, shown.length + 1)), 60)
      } else {
        timeout = setTimeout(() => setPhase('pause'), 1700)
      }
    } else if (phase === 'pause') {
      timeout = setTimeout(() => setPhase('deleting'), 200)
    } else {
      if (shown.length > 0) {
        timeout = setTimeout(() => setShown(shown.slice(0, -1)), 30)
      } else {
        setIdx((idx + 1) % ROTATE.length)
        setPhase('typing')
      }
    }
    return () => clearTimeout(timeout)
  }, [shown, phase, idx])

  return (
    <header className="relative px-6 pt-14 md:pt-24 pb-24 md:pb-32 overflow-hidden">
      <div className="absolute inset-0 mesh-bg pointer-events-none opacity-90" />
      <ParticleField count={30} />
      <div className="relative max-w-5xl mx-auto text-center">
        <div className="inline-flex items-center gap-2 px-4 py-1.5 rounded-full text-xs font-bold mb-6 backdrop-blur-md"
             style={{ background: 'rgba(255,170,61,0.18)', border: '1px solid rgba(255,170,61,0.40)', color: '#ffaa3d' }}>
          <span className="w-1.5 h-1.5 rounded-full animate-pulse" style={{ background: '#ffaa3d' }} />
          🛠 IN PLANUNG · BETA-TESTER GESUCHT · 20 % RABATT
        </div>
        <h1 className="text-4xl md:text-7xl font-bold tracking-tight leading-[1.05] mb-6">
          Daten in <span className="heading-fade cursor-blink">{shown || ' '}</span>
          <br />
          <span className="text-app-text-dim text-3xl md:text-5xl font-medium">— nicht in der Cloud.</span>
        </h1>
        <p className="text-base md:text-lg text-app-text-dim max-w-2xl mx-auto mb-10 leading-relaxed">
          Die komplette CustoSoft-Plattform self-hosted auf <strong className="text-app-text">Ihrem Server</strong>:
          Stempeluhr · Zeitenübersicht · Schichtplan · Mitarbeiter-Akten · Krankmeldungen ·
          Bewerbungsmanager · Chat. Keine Cloud, keine externe Datenverarbeitung —
          Sie betreiben den Server selbst.
        </p>
        <div className="flex flex-wrap items-center justify-center gap-3">
          <button
            type="button"
            onClick={onOpenBeta}
            className="inline-flex items-center gap-2 px-7 py-3.5 rounded-full font-semibold text-white text-base mega-glow"
            style={{ background: 'linear-gradient(135deg, #8a44ee, #3a62ff)' }}
          >
            🧪 Jetzt Beta-Tester werden · 20 % sparen
          </button>
          <a
            href="#pricing"
            className="inline-flex items-center gap-2 px-7 py-3.5 rounded-full font-semibold text-app-text border-2 border-app-border hover:bg-app-elev-2/60 transition"
          >
            Preise ansehen ↓
          </a>
        </div>
        <div className="mt-12 flex flex-wrap items-center justify-center gap-6 text-xs text-app-text-mute">
          <span className="inline-flex items-center gap-1.5">🇩🇪 Made in Germany</span>
          <span className="inline-flex items-center gap-1.5">📦 Linux · Windows · macOS · Docker</span>
          <span className="inline-flex items-center gap-1.5">⚙ Selbst-Install in 2–8 h</span>
          <span className="inline-flex items-center gap-1.5">📥 Einmal-Lizenz · keine Subscription</span>
        </div>
      </div>
    </header>
  )
}

// ════════════════════════════════════════════════════════════════════════════
// 3. TRUST BAR (marquee)
// ════════════════════════════════════════════════════════════════════════════
function TrustBar() {
  const items = [
    'KEINE CLOUD', 'IHRE DATEN BLEIBEN BEI IHNEN', 'EINMAL-LIZENZ',
    'AUF IHREM SERVER', 'GITHUB-REPO-ZUGANG',
    'BUG-FIXES INKL.', 'DATENSCHUTZ-UPDATES INKL.', 'WEBAPP IM BROWSER',
    'STUNDEN-ÜBERSICHT · CSV-EXPORT', 'KEINE LOHN-BERECHNUNG',
    'BETA · SETUP KOSTENLOS', 'BETA · 100 % REMOTE-SUPPORT', 'BETA · 20 % RABATT',
  ]
  // Zweimal duplizieren für nahtloses Marquee
  const marquee = [...items, ...items]
  return (
    <section className="relative py-6 border-y" style={{ borderColor: 'var(--border)', background: 'rgba(0,0,0,0.20)' }}>
      <div className="marquee-host overflow-hidden">
        <div className="marquee-track gap-12 whitespace-nowrap">
          {marquee.map((s, i) => (
            <span key={i} className="text-xs font-bold tracking-[0.2em] text-app-text-mute flex items-center gap-12">
              {s}
              <span className="text-accent">●</span>
            </span>
          ))}
        </div>
      </div>
    </section>
  )
}

// ════════════════════════════════════════════════════════════════════════════
// 4. FEATURES
// ════════════════════════════════════════════════════════════════════════════
function Features() {
  const FEATS = [
    { icon: '⏱',  t: 'Stempeluhr',           d: 'Live-Punch · Pausen · manuelle Korrektur durch Manager.' },
    { icon: '📊', t: 'Zeitenübersicht',      d: 'Pro-Mitarbeiter-Auswertung · Wochen/Monats-Stats · CSV-Export. Keine Lohn-Berechnung — saubere Stunden-Übersicht.' },
    { icon: '📅', t: 'Schichtplan',          d: 'Wochengrid · Drag-Doppelklick · Approval-Workflow für kurzfristige Änderungen.' },
    { icon: '🩺', t: 'Krankmeldungs-Inbox',  d: 'PDF-Anhang · Manager-Approval · 1-Klick „In Mitarbeiter-Akte übernehmen".' },
    { icon: '📁', t: 'Mitarbeiter-Akten',    d: 'Dokumenten-Verwaltung · Sichtbarkeits-Stufen · Rechte-System je Datei.' },
    { icon: '💼', t: 'Bewerbungsmanager',    d: 'Public-Apply-Links · CV-Upload · Workflow-Status · Inbox mit Notizen.' },
    { icon: '👥', t: 'Multi-Team',           d: 'Mehrere unabhängige Teams pro Server — eine Plattform fürs ganze Unternehmen.' },
    { icon: '💬', t: 'Chat',                 d: '1:1 + Gruppen · Datei-Anhänge · System-Channels für Schichtplan-Updates.' },
    { icon: '📊', t: 'Admin-Dashboard',      d: 'Live-Metriken · Aktive Nutzer · Bug-Tracker · Wochen-Briefing per Email.' },
  ]
  return (
    <section id="features" className="relative py-24 px-6">
      <div className="max-w-6xl mx-auto">
        <div className="text-center mb-12 reveal">
          <div className="inline-block text-xs font-bold tracking-widest text-accent mb-2">FEATURES</div>
          <h2 className="text-3xl md:text-5xl font-bold heading-fade">Alles was Ihr Team braucht.</h2>
          <p className="mt-3 text-app-text-dim max-w-2xl mx-auto">
            Identischer Funktionsumfang wie die SaaS-Variante. Sie verlieren nichts durch das Selbsthosten —
            außer den Datenstrom in fremde Cloudserver.
          </p>
        </div>
        <div className="grid md:grid-cols-3 gap-4">
          {FEATS.map((f, i) => (
            <div
              key={f.t}
              className={'glass p-6 rounded-2xl tilt-hover reveal reveal-stagger-' + ((i % 4) + 1)}
            >
              <div className="text-3xl mb-3">{f.icon}</div>
              <div className="text-base font-bold mb-1">{f.t}</div>
              <div className="text-sm text-app-text-dim leading-relaxed">{f.d}</div>
            </div>
          ))}
        </div>
      </div>
    </section>
  )
}

// ════════════════════════════════════════════════════════════════════════════
// 6. DEPLOYMENT
// ════════════════════════════════════════════════════════════════════════════
function Deployment() {
  const FORMATS = [
    { icon: '🐳', t: 'Docker / Compose',        d: 'docker compose up -d', sub: 'für die meisten Sysadmins der schnellste Weg' },
    { icon: '🐧', t: '.deb · .rpm',             d: 'apt install custosoft-server', sub: 'Debian · Ubuntu · RHEL · SUSE' },
    { icon: '🪟', t: 'Windows-MSI',             d: 'CustoSoftServer-1.0.msi', sub: 'signiert · Group-Policy-deploybar' },
    { icon: '🍎', t: 'macOS-PKG',               d: 'CustoSoftServer-1.0.pkg', sub: 'notarisiert · Apple Silicon + Intel' },
    { icon: '📦', t: 'Standalone-Binary',       d: '~80 MB single file', sub: 'für Air-Gapped · ohne Container' },
  ]
  return (
    <section className="relative py-24 px-6">
      <div className="max-w-6xl mx-auto">
        <div className="text-center mb-12 reveal">
          <div className="inline-block text-xs font-bold tracking-widest text-accent mb-2">DEPLOYMENT</div>
          <h2 className="text-3xl md:text-5xl font-bold heading-fade">5 Wege auf Ihre Hardware.</h2>
          <p className="mt-3 text-app-text-dim max-w-2xl mx-auto">
            Der Installer-Pfad richtet sich nach Ihrer IT-Realität. Egal ob Container-Strategie oder klassische
            VM — wir liefern das passende Format.
          </p>
        </div>
        <div className="grid md:grid-cols-5 gap-3">
          {FORMATS.map((f, i) => (
            <div
              key={f.t}
              className={'p-5 rounded-2xl text-center reveal reveal-stagger-' + ((i % 4) + 1)}
              style={{
                background: 'rgba(255,255,255,0.04)',
                border: '1px solid rgba(255,255,255,0.10)',
              }}
            >
              <div className="text-3xl mb-3">{f.icon}</div>
              <div className="text-sm font-bold mb-1">{f.t}</div>
              <div className="text-[11px] font-mono text-accent mb-1">{f.d}</div>
              <div className="text-[10px] text-app-text-mute">{f.sub}</div>
            </div>
          ))}
        </div>
      </div>
    </section>
  )
}

// ════════════════════════════════════════════════════════════════════════════
// 8. PROCESS / TIMELINE
// ════════════════════════════════════════════════════════════════════════════
function Process() {
  const STEPS = [
    { d: 'Tag 0',     t: 'Anfrage + Demo',        c: 'Kostenloser 30-Min-Termin per Video — wir zeigen die Plattform live, Sie schildern Ihre Anforderungen.' },
    { d: 'Tag 1–3',   t: 'Angebot + Lizenz',      c: 'Maßgeschneidertes Angebot. Bei Auftrag erhalten Sie die Lizenz-Datei, Installer und Setup-Doku per Email.' },
    { d: 'Woche 1',   t: 'Eigenständige Installation', c: 'Ihr Sysadmin installiert auf Ihrem Server — Docker oder Native-Installer, Doku führt Schritt für Schritt durch. Standard-Setup in 2–4 Stunden.' },
    { d: 'Bei Bedarf', t: 'Remote-Support',       c: 'Falls Fragen aufkommen: kostenpflichtige Remote-Setup-Hilfe (990 € Pauschale) per Video-Session — optional, kein Muss.' },
    { d: 'Go-Live',   t: 'Eigenbetrieb',          c: 'Sie betreiben den Server selbst — auf Ihrem eigenen Server, ohne Cloud-Bindung. Updates ziehen Sie wann Sie wollen.' },
  ]
  return (
    <section id="process" className="relative py-24 px-6 border-t" style={{ borderColor: 'var(--border)' }}>
      <div className="max-w-5xl mx-auto">
        <div className="text-center mb-14 reveal">
          <div className="inline-block text-xs font-bold tracking-widest text-accent mb-2">ABLAUF</div>
          <h2 className="text-3xl md:text-5xl font-bold heading-fade">Von Anfrage bis Go-Live in ~3 Wochen.</h2>
          <p className="mt-3 text-app-text-dim max-w-2xl mx-auto">
            Realistische Zeitschätzung für eine Standard-Installation mit ≤ 100 Mitarbeitern. Komplexere
            Setups (große AD-Forests, mehrere Standorte, Custom-Branding) entsprechend länger.
          </p>
        </div>
        <div className="relative">
          <div className="absolute left-7 md:left-1/2 top-2 bottom-2 w-px md:-translate-x-1/2"
               style={{ background: 'linear-gradient(to bottom, transparent, rgba(138,68,238,0.50), transparent)' }} />
          <div className="space-y-8">
            {STEPS.map((s, i) => (
              <div key={s.t} className={'relative reveal reveal-stagger-' + ((i % 4) + 1) +
                ' flex md:grid md:grid-cols-2 md:gap-8 items-start'}>
                {/* Timeline dot */}
                <div className="absolute left-7 md:left-1/2 -translate-x-1/2 top-3 w-3 h-3 rounded-full"
                     style={{ background: '#c084fc', boxShadow: '0 0 0 4px rgba(138,68,238,0.20)' }} />
                <div className={i % 2 === 0 ? 'md:text-right md:pr-8 ml-16 md:ml-0' : 'md:col-start-2 md:pl-8 ml-16 md:ml-0'}>
                  <div className="text-xs font-bold tracking-widest" style={{ color: '#c084fc' }}>{s.d}</div>
                  <div className="text-xl font-bold mt-1">{s.t}</div>
                  <div className="text-sm text-app-text-dim mt-2 leading-relaxed">{s.c}</div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>
    </section>
  )
}

// ════════════════════════════════════════════════════════════════════════════
// 9. PRICING
// ════════════════════════════════════════════════════════════════════════════
function Pricing() {
  const TIERS = [
    {
      name:  'Starter',
      price: '1.990 €',
      sub:   'einmalig',
      users: 'bis 25 Mitarbeiter',
      maint: '+ 400 €/Jahr Update-Abo',
      time:  '~ 2–4 h Selbst-Install',
      feats: [
        'SQLite-DB · zero-config',
        'Docker oder Native-Installer',
        'GitHub-Repo-Zugang (Basis-Build)',
        'Auto-Backups · Email-Support',
      ],
      hl:    false,
    },
    {
      name:  'Business',
      price: '4.990 €',
      sub:   'einmalig',
      users: 'bis 100 Mitarbeiter',
      maint: '+ 1.000 €/Jahr Update-Abo',
      time:  '~ 4–8 h Selbst-Install',
      feats: [
        'SQLite oder Postgres',
        'Mehrere Teams pro Server',
        'GitHub-Repo-Zugang (Basis-Build)',
        'Email + Telefon-Support',
      ],
      hl:    true,
    },
    {
      name:  'Enterprise',
      price: '12.990 €',
      sub:   'einmalig',
      users: 'bis 500 Mitarbeiter',
      maint: '+ 2.600 €/Jahr Update-Abo',
      time:  '~ 1–2 Tage Selbst-Install',
      feats: [
        'Postgres empfohlen',
        'GitHub-Repo-Zugang (Basis-Build)',
        'Custom-Branding möglich (auf Aufwand)',
        '4 h SLA für Support',
      ],
      hl:    false,
    },
    {
      name:  'Site License',
      price: '24.990 €',
      sub:   'einmalig',
      users: 'unbegrenzte User',
      maint: '+ 5.000 €/Jahr Update-Abo',
      time:  '~ 2–3 Tage Selbst-Install',
      feats: [
        'Alles aus Enterprise',
        'Mehrere Standorte',
        'GitHub-Repo-Zugang (Basis-Build)',
        '1 h SLA für Support',
      ],
      hl:    false,
    },
  ]
  return (
    <section id="pricing" className="relative py-24 px-6">
      <div className="max-w-7xl mx-auto">
        <div className="text-center mb-14 reveal">
          <div className="inline-block text-xs font-bold tracking-widest text-accent mb-2">PREISE</div>
          <h2 className="text-3xl md:text-5xl font-bold heading-fade">Einmal kaufen · für immer nutzen.</h2>
          <p className="mt-3 text-app-text-dim max-w-2xl mx-auto">
            Keine Subscription-Falle. Eine perpetuelle Lizenz pro Tier — Sie bezahlen einmalig und nutzen sie
            so lange Sie wollen. Updates kommen über den optionalen Wartungsvertrag.
          </p>
        </div>
        <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-4">
          {TIERS.map((t, i) => (
            <div
              key={t.name}
              className={
                'p-7 rounded-3xl tilt-3d reveal reveal-stagger-' + ((i % 4) + 1) +
                ' relative ' + (t.hl ? 'border-2' : 'border')
              }
              style={{
                background: t.hl
                  ? 'linear-gradient(135deg, rgba(138,68,238,0.18), rgba(58,98,255,0.10))'
                  : 'rgba(255,255,255,0.04)',
                borderColor: t.hl ? 'rgba(138,68,238,0.50)' : 'rgba(255,255,255,0.10)',
              }}
            >
              {t.hl && (
                <div className="absolute -top-3 left-1/2 -translate-x-1/2 px-3 py-1 rounded-full text-[10px] font-bold tracking-widest text-white"
                     style={{ background: 'linear-gradient(135deg, #8a44ee, #3a62ff)' }}>
                  BELIEBT
                </div>
              )}
              <div className="text-xs font-bold tracking-widest text-app-text-mute mb-3">{t.name.toUpperCase()}</div>
              <div className="flex items-baseline gap-2 mb-1">
                <span className="text-3xl font-bold heading-fade">{t.price}</span>
                <span className="text-xs text-app-text-mute">{t.sub}</span>
              </div>
              <div className="text-sm font-semibold mb-1">{t.users}</div>
              <div className="text-xs text-app-text-mute mb-2">{t.maint}</div>
              <div className="text-xs text-accent mb-5">⏱ {t.time}</div>
              <ul className="space-y-2 mb-6">
                {t.feats.map(f => (
                  <li key={f} className="flex items-start gap-2 text-sm">
                    <span className="text-green-400 shrink-0 mt-0.5">✓</span>
                    <span className="text-app-text-dim">{f}</span>
                  </li>
                ))}
              </ul>
              <a
                href={`mailto:${CONTACT_EMAIL}?subject=Anfrage%20${encodeURIComponent(t.name)}-Lizenz&body=Hallo%2C%0Aich%20interessiere%20mich%20für%20die%20${encodeURIComponent(t.name)}-Lizenz.%0A%0AFirma%3A%20%0AMitarbeiter%3A%20%0AGewünschter%20Setup-Zeitraum%3A%20%0A`}
                className={
                  'block w-full text-center py-2.5 rounded-full text-sm font-semibold transition ' +
                  (t.hl ? 'text-white' : 'border-2 border-app-border hover:bg-app-elev-2/60')
                }
                style={t.hl ? { background: 'linear-gradient(135deg, #8a44ee, #3a62ff)' } : undefined}
              >
                Anfragen
              </a>
            </div>
          ))}
        </div>
        <div className="mt-10 text-center text-xs text-app-text-mute reveal">
          Optionale Remote-Setup-Hilfe 990 € · Sie hosten selbst auf Ihrem Server · Alle Preise zzgl. 19 % USt. · Erste Demo kostenlos
        </div>
      </div>
    </section>
  )
}

// ════════════════════════════════════════════════════════════════════════════
// 9.5 UPDATE-ABO + GARANTIE
// ════════════════════════════════════════════════════════════════════════════
function UpdateAbo() {
  const INCL = [
    { icon: '🔧', t: 'Bug-Fixes',                d: 'Alle Errors der Standard-Version werden mit Patches behoben — Sie ziehen das Update aus dem Repo.' },
    { icon: '🛡', t: 'Datenschutz-Updates',      d: 'Anpassungen an neue DSGVO-Vorgaben oder Sicherheits-CVEs werden zeitnah eingespielt.' },
    { icon: '📦', t: 'GitHub-Repo-Zugang',       d: 'Read-Access auf das private Repository des Basis-Builds. Pull, audit, deploy in Ihrem Tempo.' },
    { icon: '🔐', t: 'Sicherheits-Patches',      d: 'Kritische Vulnerabilities (auch in Drittanbieter-Libs) werden über das Repo bereitgestellt.' },
  ]
  const EXCL = [
    { icon: '🎨', t: 'Custom-Branding',          d: 'Eigenes Logo, Farben, Login-Theme — auf Aufwand abrechenbar (Tagessatz).' },
    { icon: '🧩', t: 'Sonder-Integrationen',     d: 'Anbindung an proprietäre ERP-Systeme, Zeitwirtschaft-Software etc. — separates Projekt.' },
    { icon: '📋', t: 'Individuelle Reports',     d: 'Maßgeschneiderte CSV-/PDF-Exporte über die Standard-Vorlage hinaus — Aufwands-basiert.' },
    { icon: '🆕', t: 'Neue Features auf Wunsch', d: 'Funktionen die nicht in der Standard-Roadmap sind — als Auftragsentwicklung beauftragbar.' },
  ]
  return (
    <section id="updates" className="relative py-24 px-6 border-t" style={{ borderColor: 'var(--border)' }}>
      <div className="max-w-6xl mx-auto">
        <div className="text-center mb-12 reveal">
          <div className="inline-block text-xs font-bold tracking-widest text-accent mb-2">UPDATE-ABO &amp; GARANTIE</div>
          <h2 className="text-3xl md:text-5xl font-bold heading-fade">Was im jährlichen Update-Abo steckt.</h2>
          <p className="mt-3 text-app-text-dim max-w-2xl mx-auto">
            Eine perpetuelle Lizenz, ein optionales Update-Abo. Das Abo deckt Bug-Fixes und Sicherheits-Updates ab —
            individuelle Anpassungen sind klar getrennt und werden auf Aufwand abgerechnet.
          </p>
        </div>

        <div className="grid md:grid-cols-2 gap-4">
          {/* INKLUDIERT */}
          <div className="rounded-3xl p-7 reveal reveal-stagger-1"
               style={{
                 background: 'linear-gradient(135deg, rgba(51,214,112,0.10), rgba(51,214,112,0.04))',
                 border: '2px solid rgba(51,214,112,0.30)',
               }}>
            <div className="flex items-center gap-2 mb-5">
              <span className="text-2xl">✅</span>
              <span className="text-xs font-bold tracking-widest" style={{ color: '#33d670' }}>IM UPDATE-ABO ENTHALTEN</span>
            </div>
            <ul className="space-y-4">
              {INCL.map(i => (
                <li key={i.t} className="flex items-start gap-3">
                  <span className="text-2xl shrink-0">{i.icon}</span>
                  <div>
                    <div className="font-bold text-sm">{i.t}</div>
                    <div className="text-xs text-app-text-dim leading-relaxed mt-0.5">{i.d}</div>
                  </div>
                </li>
              ))}
            </ul>
            <div className="mt-6 pt-5 border-t text-xs text-app-text-mute" style={{ borderColor: 'rgba(51,214,112,0.20)' }}>
              Garantie greift solange diese Major-Version vom Hersteller supported wird (Standard 2 Jahre ab Release).
            </div>
          </div>

          {/* NICHT ENTHALTEN */}
          <div className="rounded-3xl p-7 reveal reveal-stagger-2"
               style={{
                 background: 'rgba(255,255,255,0.04)',
                 border: '1px solid rgba(255,255,255,0.10)',
               }}>
            <div className="flex items-center gap-2 mb-5">
              <span className="text-2xl">⚙</span>
              <span className="text-xs font-bold tracking-widest text-app-text-mute">NICHT ENTHALTEN · AUF AUFWAND</span>
            </div>
            <ul className="space-y-4">
              {EXCL.map(e => (
                <li key={e.t} className="flex items-start gap-3 opacity-90">
                  <span className="text-2xl shrink-0">{e.icon}</span>
                  <div>
                    <div className="font-bold text-sm">{e.t}</div>
                    <div className="text-xs text-app-text-dim leading-relaxed mt-0.5">{e.d}</div>
                  </div>
                </li>
              ))}
            </ul>
            <div className="mt-6 pt-5 border-t text-xs text-app-text-mute" style={{ borderColor: 'var(--border)' }}>
              Personalisierte Anfertigungen werden separat per Angebot beauftragt.
              Tagessatz auf Anfrage.
            </div>
          </div>
        </div>

        <div className="mt-8 text-center text-xs text-app-text-mute reveal">
          🔓 Update-Abo jederzeit kündbar · Lizenz bleibt perpetuell gültig auch ohne Abo (mit der Version zum Kündigungs-Stand)
        </div>
      </div>
    </section>
  )
}

// ════════════════════════════════════════════════════════════════════════════
// 10. FAQ
// ════════════════════════════════════════════════════════════════════════════
function FAQ() {
  const QA = [
    { q: 'Was bekommen Beta-Tester über die 20 % Rabatt hinaus?',
      a: 'Beta-Tester die eine Anzahlung leisten erhalten zusätzlich: kostenlosen Aufsetz-Service (wir installieren den Server bei euch — sonst 990 € Pauschale), kostenloses Testing-Service während der gesamten Beta-Phase, sowie vollen 100-%-Remote-Support direkt vom Dev-Team (keine Tickets, keine Aufpreise). Plus Mitsprache an der Feature-Roadmap — die im Anmelde-Fragebogen angekreuzten Module fließen direkt in die Priorisierung.' },
    { q: 'Was bekommen wir mit dem Update-Abo?',
      a: 'Zugang zum privaten GitHub-Repository des Basis-Builds plus alle Updates und Fixes solange diese Major-Version vom Hersteller supported wird (idR 2 Jahre ab Release). Updates sind: Bug-Fixes, Sicherheits-Patches und Datenschutz-Anpassungen (z.B. neue DSGVO-Vorgaben). NICHT enthalten sind individuelle Anpassungen oder Custom-Features — die sind separat beauftragbar.' },
    { q: 'Was ist von der Garantie abgedeckt?',
      a: 'Garantie-Fixes nur bei nachweisbaren Errors / Bugs der Standard-Version, sowie Datenschutz- und Sicherheits-Updates. Personalisierte Anfertigungen (Custom-Branding, individuelle Reports, Sonder-Integrationen) liegen außerhalb der Garantie und werden auf Aufwand abgerechnet.' },
    { q: 'Wer installiert den Server?',
      a: 'Sie selbst, beziehungsweise Ihr Sysadmin. Wir liefern fertige Installer (Docker, .deb, .rpm, .msi, .pkg) plus Schritt-für-Schritt-Doku — Standard-Setup ist in 2–4 Stunden erledigt. Wenn Sie lieber Hilfe wollen: optionale Remote-Setup-Session 990 € per Video.' },
    { q: 'Macht das System Lohn-Berechnung?',
      a: 'Nein — bewusst nicht. CustoSoft Server liefert eine saubere Zeiten- und Schichten-Übersicht inkl. CSV-Export. Die eigentliche Lohnabrechnung machen Sie weiter mit Ihrer bestehenden Lohn-Software (DATEV, Lexware, etc.) — die Stunden-Exporte passen in jedes Standard-Format.' },
    { q: 'Brauchen wir Internet-Verbindung im Betrieb?',
      a: 'Nein. Der Server läuft komplett offline. Nur wenn Sie automatische Updates wollen, pingt der Server unsere Update-API einmal pro Tag. Diese Update-Pings sind komplett deaktivierbar.' },
    { q: 'Was passiert wenn die Update-Verlängerung ausläuft?',
      a: 'Die Lizenz bleibt perpetuell gültig — Sie können den Server unbegrenzt weiter benutzen mit der Version die zum Ende der Update-Verlängerung aktuell war. Nur neuere Major-Versionen sind dann gesperrt, Sie können das Update-Abo jederzeit reaktivieren.' },
    { q: 'Wo werden die Daten gespeichert?',
      a: 'Komplett auf Ihrem Server: SQLite-Datei oder Postgres-DB, Datei-Anhänge im gleichen Daten-Verzeichnis. Backup-Strategie liegt bei Ihnen — wir liefern Skripte mit, der Standard-Cron läuft täglich 03:00 mit 7-Tage-Rotation.' },
    { q: 'Welche Hardware brauchen wir?',
      a: 'Minimum: 2 vCPU, 4 GB RAM, 20 GB SSD für ≤ 50 Mitarbeiter. Für 500 Mitarbeiter: 4 vCPU, 8 GB RAM, 100 GB SSD. Läuft auf jeder Linux-, Windows-Server- oder macOS-Maschine — auch auf einem Raspberry Pi 4 für Klein-Setups.' },
    { q: 'Bekommen wir die mobile App auch?',
      a: 'Die Webapp ist voll mobile-responsive und läuft sauber im iPhone-, iPad- und Android-Browser. Eine native iOS/Mac-App ist für CustoSoft Server nicht vorgesehen — die Browser-Variante deckt alle Funktionen ab.' },
  ]
  return (
    <section id="faq" className="relative py-24 px-6 border-t" style={{ borderColor: 'var(--border)' }}>
      <div className="max-w-3xl mx-auto">
        <div className="text-center mb-12 reveal">
          <div className="inline-block text-xs font-bold tracking-widest text-accent mb-2">FAQ</div>
          <h2 className="text-3xl md:text-5xl font-bold heading-fade">Häufige Fragen.</h2>
        </div>
        <div className="space-y-3">
          {QA.map((qa, i) => <FaqItem key={i} {...qa} />)}
        </div>
      </div>
    </section>
  )
}

function FaqItem({ q, a }: { q: string; a: string }) {
  const [open, setOpen] = useState(false)
  return (
    <div className="reveal rounded-2xl overflow-hidden border" style={{ borderColor: 'var(--border)' }}>
      <button
        onClick={() => setOpen(o => !o)}
        className="w-full text-left p-5 flex justify-between items-center gap-4 hover:bg-app-elev-2/40 transition"
      >
        <span className="font-semibold">{q}</span>
        <span className="text-app-text-mute text-xl shrink-0 transition-transform"
              style={{ transform: open ? 'rotate(45deg)' : 'rotate(0)' }}>+</span>
      </button>
      <div className={'overflow-hidden transition-[max-height,opacity] duration-300 ' +
                     (open ? 'max-h-96 opacity-100' : 'max-h-0 opacity-0')}>
        <p className="px-5 pb-5 text-sm text-app-text-dim leading-relaxed">{a}</p>
      </div>
    </div>
  )
}

// ════════════════════════════════════════════════════════════════════════════
// 11. FINAL CTA
// ════════════════════════════════════════════════════════════════════════════
function FinalCTA({ onOpenBeta }: { onOpenBeta: () => void }) {
  return (
    <section className="relative py-24 px-6 overflow-hidden">
      <div className="absolute inset-0 mesh-bg opacity-40 pointer-events-none" />
      <div className="relative max-w-3xl mx-auto text-center reveal">
        <h2 className="text-3xl md:text-5xl font-bold heading-fade mb-4">
          Werden Sie Beta-Tester — sichern Sie sich 20 % Rabatt.
        </h2>
        <p className="text-app-text-dim mb-10">
          Konzept noch in Planung. Beta-Tester finanzieren das Projekt mit einer Anzahlung mit und erhalten dafür
          bei Fertigstellung 20 % auf den Endpreis. Wir beantworten jede Anfrage binnen 48 h.
        </p>
        <div className="grid sm:grid-cols-2 gap-3 max-w-xl mx-auto mb-8">
          <button
            type="button"
            onClick={onOpenBeta}
            className="px-6 py-4 rounded-2xl font-semibold text-white text-base mega-glow"
            style={{ background: 'linear-gradient(135deg, #8a44ee, #3a62ff)' }}
          >
            🧪 Beta-Anmeldung ausfüllen
          </button>
          <a
            href={`mailto:${CONTACT_EMAIL}?subject=R%C3%BCckfrage%20CustoSoft%20Server`}
            className="px-6 py-4 rounded-2xl font-semibold border-2 border-app-border hover:bg-app-elev-2/60 transition"
          >
            ✉ {CONTACT_EMAIL}
          </a>
        </div>
        <div className="text-xs text-app-text-mute space-y-1">
          <div>Antwort innerhalb 48 h · Mo–Fr 9–17 Uhr CET</div>
          <div>Ersatz-Kontakt: <a className="hover:text-app-text underline" href={`mailto:${CONTACT_FALLBACK}`}>{CONTACT_FALLBACK}</a></div>
        </div>
      </div>
    </section>
  )
}

// ════════════════════════════════════════════════════════════════════════════
// 10.5 BETA-TESTER-SECTION — Eyecatcher für die /server-Page
// ════════════════════════════════════════════════════════════════════════════
function BetaTester({ onOpenBeta }: { onOpenBeta: () => void }) {
  return (
    <section id="beta" className="relative py-24 px-6 overflow-hidden border-t" style={{ borderColor: 'var(--border)' }}>
      <div className="absolute inset-0 pointer-events-none"
           style={{
             background: 'radial-gradient(ellipse at center, rgba(255,170,61,0.10), transparent 60%)',
           }} />
      <div className="relative max-w-5xl mx-auto">
        <div className="text-center mb-14 reveal">
          <div className="inline-block text-xs font-bold tracking-widest mb-2" style={{ color: '#ffaa3d' }}>BETA-PROGRAMM</div>
          <h2 className="text-3xl md:text-5xl font-bold heading-fade">
            Erste 20 Beta-Tester · 20 % Rabatt · Setup + Support inklusive.
          </h2>
          <p className="mt-3 text-app-text-dim max-w-2xl mx-auto">
            CustoSoft Server ist gerade in der Konzept- und Build-Phase. Wer früh dabei ist und eine Anzahlung leistet,
            bekommt bei Fertigstellung <strong className="text-white">20 % Rabatt</strong>, dazu unseren
            <strong className="text-white"> Aufsetz-Service kostenlos</strong>, kostenloses Testing
            und <strong className="text-white">100 % Remote-Support</strong> während der gesamten Beta-Phase.
          </p>
        </div>

        <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-4 mb-10">
          {/* Karte 1 — Anzahlung */}
          <div className="glass p-6 rounded-2xl reveal reveal-stagger-1">
            <div className="text-3xl mb-3">💰</div>
            <div className="font-bold mb-1">Slot reservieren</div>
            <div className="text-sm text-app-text-dim leading-relaxed">
              Anzahlung (Höhe wird im Angebot festgelegt) sichert deinen Beta-Slot. Restbetrag erst bei Auslieferung.
            </div>
          </div>

          {/* Karte 2 — 20 % Rabatt */}
          <div className="glass p-6 rounded-2xl reveal reveal-stagger-2">
            <div className="text-3xl mb-3">🎁</div>
            <div className="font-bold mb-1">20 % Rabatt</div>
            <div className="text-sm text-app-text-dim leading-relaxed">
              Bei Fertigstellung zahlst du nur 80 % des Tier-Preises — garantiert für die ersten 20 Beta-Tester.
            </div>
          </div>

          {/* Karte 3 — Setup-Service kostenlos (NEU) */}
          <div
            className="p-6 rounded-2xl reveal reveal-stagger-3 relative overflow-hidden"
            style={{
              background: 'linear-gradient(135deg, rgba(51,214,112,0.12), rgba(51,214,112,0.04))',
              border: '1px solid rgba(51,214,112,0.30)',
            }}
          >
            <div className="absolute top-3 right-3 px-2 py-0.5 rounded-full text-[9px] font-bold tracking-widest"
                 style={{ background: 'rgba(51,214,112,0.25)', color: '#33d670' }}>
              GRATIS
            </div>
            <div className="text-3xl mb-3">🛠</div>
            <div className="font-bold mb-1">Aufsetz-&nbsp;+ Test-Service</div>
            <div className="text-sm text-app-text-dim leading-relaxed">
              Wir installieren den Server bei dir (sonst <span className="line-through opacity-70">990 €</span>) und
              übernehmen das komplette Beta-Testing — kostenlos.
            </div>
          </div>

          {/* Karte 4 — 100 % Remote-Support (NEU) */}
          <div
            className="p-6 rounded-2xl reveal reveal-stagger-4 relative overflow-hidden"
            style={{
              background: 'linear-gradient(135deg, rgba(51,214,112,0.12), rgba(51,214,112,0.04))',
              border: '1px solid rgba(51,214,112,0.30)',
            }}
          >
            <div className="absolute top-3 right-3 px-2 py-0.5 rounded-full text-[9px] font-bold tracking-widest"
                 style={{ background: 'rgba(51,214,112,0.25)', color: '#33d670' }}>
              GRATIS
            </div>
            <div className="text-3xl mb-3">🤝</div>
            <div className="font-bold mb-1">100 % Remote-Support</div>
            <div className="text-sm text-app-text-dim leading-relaxed">
              Voller Remote-Support während der gesamten Beta-Phase — direkter Draht ins Dev-Team, keine Tickets,
              keine Aufpreise.
            </div>
          </div>
        </div>

        <div className="mb-10 text-center text-xs text-app-text-mute reveal">
          🗺 Plus Mitspracherecht in der Roadmap — deine angekreuzten Module fließen direkt in die Priorisierung.
        </div>

        <div className="text-center reveal">
          <button
            type="button"
            onClick={onOpenBeta}
            className="inline-flex items-center gap-2 px-8 py-4 rounded-full font-semibold text-white text-base mega-glow"
            style={{ background: 'linear-gradient(135deg, #ffaa3d, #f2404c, #8a44ee)' }}
          >
            🧪 Fragebogen ausfüllen · Beta-Slot reservieren
          </button>
          <div className="mt-4 text-xs text-app-text-mute">
            Unverbindlich · keine sofortige Zahlung · wir melden uns binnen 48 h mit Details und individuellem Angebot.
          </div>
        </div>
      </div>
    </section>
  )
}

// ════════════════════════════════════════════════════════════════════════════
// 10.6 BETA-MODAL — Anmelde-Fragebogen
// ════════════════════════════════════════════════════════════════════════════

const BETA_FEATURES: { slug: string; label: string; hint?: string }[] = [
  { slug: 'punch-clock',     label: 'Stempeluhr',           hint: 'Live-Punch + Pausen' },
  { slug: 'time-overview',   label: 'Zeitenübersicht',      hint: 'CSV-Export, kompatibel mit DATEV/Lexware' },
  { slug: 'shifts',          label: 'Schichtplan',          hint: 'Wochengrid + Approvals' },
  { slug: 'sick-leaves',     label: 'Krankmeldungs-Inbox',  hint: 'PDF-Anhänge + Approval' },
  { slug: 'employee-files',  label: 'Mitarbeiter-Akten',    hint: 'Dokumente + Sichtbarkeits-Stufen' },
  { slug: 'recruitment',     label: 'Bewerbungsmanager',    hint: 'Apply-Links + Inbox' },
  { slug: 'multi-team',      label: 'Multi-Team-Org',       hint: 'mehrere Teams pro Server' },
  { slug: 'chat',            label: 'Team-Chat',            hint: '1:1 + Gruppen + Anhänge' },
  { slug: 'admin-dashboard', label: 'Admin-Dashboard',      hint: 'Live-Metriken + Export' },
]

const BETA_EMPLOYEES: { value: string; label: string }[] = [
  { value: '1-10',    label: '1 – 10' },
  { value: '11-50',   label: '11 – 50' },
  { value: '51-100',  label: '51 – 100' },
  { value: '101-500', label: '101 – 500' },
  { value: '500+',    label: '500+' },
]

const BETA_BUDGETS: { value: string; label: string }[] = [
  { value: '<5k',    label: 'unter 5 000 €' },
  { value: '5-15k',  label: '5 000 – 15 000 €' },
  { value: '15-30k', label: '15 000 – 30 000 €' },
  { value: '>30k',   label: 'über 30 000 €' },
]

const BETA_TIMELINES: { value: string; label: string }[] = [
  { value: 'asap', label: 'Sofort / ASAP' },
  { value: '3m',   label: 'in ~3 Monaten' },
  { value: '6m',   label: 'in ~6 Monaten' },
  { value: '12m',  label: 'in ~12 Monaten' },
  { value: 'flex', label: 'flexibel' },
]

interface BetaModalProps { onClose: () => void }
interface BetaForm {
  email:         string
  company:       string
  contactName:   string
  phone:         string
  employeeCount: string
  budget:        string
  timeline:      string
  features:      string[]
  depositOk:     boolean
  notes:         string
}

function BetaModal({ onClose }: BetaModalProps) {
  const [form, setForm] = useState<BetaForm>({
    email: '', company: '', contactName: '', phone: '',
    employeeCount: '', budget: '', timeline: '',
    features: [], depositOk: true, notes: '',
  })
  const [submitting, setSubmitting] = useState(false)
  const [error, setError]           = useState<string | null>(null)
  const [success, setSuccess]       = useState(false)

  // ESC schließt Modal
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => { if (e.key === 'Escape') onClose() }
    window.addEventListener('keydown', onKey)
    // Body-Scroll sperren während Modal offen ist
    const prevOverflow = document.body.style.overflow
    document.body.style.overflow = 'hidden'
    return () => {
      window.removeEventListener('keydown', onKey)
      document.body.style.overflow = prevOverflow
    }
  }, [onClose])

  function toggleFeature(slug: string) {
    setForm(f => ({
      ...f,
      features: f.features.includes(slug)
        ? f.features.filter(s => s !== slug)
        : [...f.features, slug],
    }))
  }

  async function submit(e: React.FormEvent) {
    e.preventDefault()
    setError(null)
    if (!form.email.trim()) { setError('Bitte E-Mail-Adresse angeben.'); return }
    setSubmitting(true)
    try {
      await api.post<{ ok: boolean; id: number }>('/server-interest', {
        email:         form.email.trim(),
        company:       form.company.trim()     || null,
        contactName:   form.contactName.trim() || null,
        phone:         form.phone.trim()       || null,
        employeeCount: form.employeeCount      || null,
        budget:        form.budget             || null,
        timeline:      form.timeline           || null,
        features:      form.features,
        depositOk:     form.depositOk,
        notes:         form.notes.trim()       || null,
        source:        'server-page',
      }, { noAuth: true })
      setSuccess(true)
    } catch (e: any) {
      setError(e?.message ?? 'Übermittlung fehlgeschlagen — bitte später nochmal versuchen.')
    } finally {
      setSubmitting(false)
    }
  }

  // Click-outside auf Backdrop
  function onBackdropClick(e: React.MouseEvent<HTMLDivElement>) {
    if (e.target === e.currentTarget) onClose()
  }

  // Modal-Layout: feste max-Höhe (92vh), interner Body scrollt, Header + Footer
  // sind sticky innerhalb des Modals. Damit bleibt der Submit-Button immer sichtbar
  // und das Modal sprengt nie den Viewport.
  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center p-3 sm:p-5"
      style={{ background: 'rgba(0,0,0,0.78)', backdropFilter: 'blur(8px)' }}
      onClick={onBackdropClick}
    >
      <div
        className="relative w-full max-w-md sm:max-w-lg rounded-2xl flex flex-col overflow-hidden"
        style={{
          maxHeight: '92vh',
          background: 'linear-gradient(180deg, rgba(40,30,60,0.96), rgba(20,15,30,0.98))',
          border: '1px solid rgba(138,68,238,0.30)',
          boxShadow: '0 30px 80px rgba(0,0,0,0.6)',
        }}
      >
        {/* ── HEADER (sticky innerhalb des Modals) ────────────────────────── */}
        <div
          className="flex items-start justify-between gap-3 px-5 sm:px-6 pt-5 pb-3 shrink-0"
          style={{ borderBottom: '1px solid rgba(255,255,255,0.08)' }}
        >
          <div className="min-w-0">
            <div className="text-[10px] font-bold tracking-widest mb-1" style={{ color: '#ffaa3d' }}>
              🧪 BETA-ANMELDUNG · 20 % RABATT
            </div>
            <h3 className="text-lg sm:text-xl font-bold heading-fade truncate">
              {success ? 'Vielen Dank!' : 'Beta-Slot reservieren'}
            </h3>
          </div>
          <button
            type="button"
            onClick={onClose}
            aria-label="Schließen"
            className="shrink-0 w-8 h-8 rounded-full text-app-text-dim hover:text-app-text hover:bg-white/10 flex items-center justify-center text-lg"
          >
            ✕
          </button>
        </div>

        {success ? (
          /* ── ERFOLG-STATE ──────────────────────────────────────────────── */
          <div className="p-6 sm:p-8 text-center overflow-y-auto">
            <div className="text-5xl mb-4">🎉</div>
            <p className="text-app-text-dim mb-3 text-sm">
              Deine Beta-Anmeldung ist eingegangen. Wir melden uns innerhalb der nächsten 48 h
              mit einem individuellen Angebot.
            </p>
            <p className="text-xs text-app-text-mute mb-6">
              Du bekommst keine automatische Bestätigungsmail — wir antworten persönlich von <strong className="text-app-text">{CONTACT_EMAIL}</strong>.
            </p>
            <button
              type="button"
              onClick={onClose}
              className="px-6 py-2.5 rounded-full font-semibold text-white text-sm"
              style={{ background: 'linear-gradient(135deg, #8a44ee, #3a62ff)' }}
            >
              Schließen
            </button>
          </div>
        ) : (
          /* ── FORM ────────────────────────────────────────────────────── */
          <form onSubmit={submit} className="flex flex-col min-h-0 flex-1">
            {/* Scroll-Body */}
            <div className="overflow-y-auto px-5 sm:px-6 py-4 flex-1">
              <p className="text-xs text-app-text-dim mb-4 leading-relaxed">
                Konzept noch in Planung. Anmeldung ist unverbindlich — wir melden uns mit einem individuellen Angebot,
                erst danach entscheidest du über die Anzahlung.
              </p>

              {/* E-Mail (Pflicht) */}
              <Field label="E-Mail-Adresse *">
                <input
                  type="email" required autoFocus
                  value={form.email}
                  onChange={e => setForm({ ...form, email: e.target.value })}
                  placeholder="dein.name@firma.de"
                  className="beta-input"
                />
              </Field>

              <div className="grid sm:grid-cols-2 gap-3">
                <Field label="Firma">
                  <input
                    type="text"
                    value={form.company}
                    onChange={e => setForm({ ...form, company: e.target.value })}
                    placeholder="Mustermann GmbH"
                    className="beta-input"
                  />
                </Field>
                <Field label="Ansprechpartner">
                  <input
                    type="text"
                    value={form.contactName}
                    onChange={e => setForm({ ...form, contactName: e.target.value })}
                    placeholder="Vor- und Nachname"
                    className="beta-input"
                  />
                </Field>
              </div>

              <Field label="Telefon (optional)">
                <input
                  type="tel"
                  value={form.phone}
                  onChange={e => setForm({ ...form, phone: e.target.value })}
                  placeholder="+49 …"
                  className="beta-input"
                />
              </Field>

              <Field label="Anzahl Mitarbeiter">
                <RadioPills
                  options={BETA_EMPLOYEES}
                  value={form.employeeCount}
                  onChange={v => setForm({ ...form, employeeCount: v })}
                />
              </Field>

              <Field label="Budget (Lizenz + Setup)">
                <RadioPills
                  options={BETA_BUDGETS}
                  value={form.budget}
                  onChange={v => setForm({ ...form, budget: v })}
                />
              </Field>

              <Field label="Wunsch-Zeitrahmen für Go-Live">
                <RadioPills
                  options={BETA_TIMELINES}
                  value={form.timeline}
                  onChange={v => setForm({ ...form, timeline: v })}
                />
              </Field>

              <Field label="Welche Module brauchen Sie?">
                <div className="grid sm:grid-cols-2 gap-2">
                  {BETA_FEATURES.map(f => {
                    const checked = form.features.includes(f.slug)
                    return (
                      <label
                        key={f.slug}
                        className="flex items-start gap-2.5 p-2.5 rounded-lg cursor-pointer transition"
                        style={{
                          background: checked ? 'rgba(138,68,238,0.15)' : 'rgba(255,255,255,0.03)',
                          border: '1px solid ' + (checked ? 'rgba(138,68,238,0.45)' : 'rgba(255,255,255,0.08)'),
                        }}
                      >
                        <input
                          type="checkbox"
                          checked={checked}
                          onChange={() => toggleFeature(f.slug)}
                          className="mt-0.5 shrink-0"
                        />
                        <div className="min-w-0">
                          <div className="text-xs font-semibold">{f.label}</div>
                          {f.hint && <div className="text-[11px] text-app-text-mute leading-snug">{f.hint}</div>}
                        </div>
                      </label>
                    )
                  })}
                </div>
              </Field>

              <Field label="Anzahlung &amp; Beta-Pakete">
                <label
                  className="flex items-start gap-3 p-3 rounded-lg cursor-pointer transition"
                  style={{
                    background: form.depositOk ? 'rgba(51,214,112,0.10)' : 'rgba(255,255,255,0.03)',
                    border: '1px solid ' + (form.depositOk ? 'rgba(51,214,112,0.35)' : 'rgba(255,255,255,0.08)'),
                  }}
                >
                  <input
                    type="checkbox"
                    checked={form.depositOk}
                    onChange={e => setForm({ ...form, depositOk: e.target.checked })}
                    className="mt-1 shrink-0"
                  />
                  <div className="min-w-0">
                    <div className="text-xs font-semibold leading-snug">
                      Ja, wir leisten eine Anzahlung und finanzieren das Projekt mit.
                    </div>
                    <ul className="text-[11px] text-app-text-dim mt-2 leading-snug space-y-1">
                      <li className="flex gap-1.5"><span style={{ color: '#33d670' }}>✓</span> 20 % Rabatt auf den Endpreis bei Fertigstellung</li>
                      <li className="flex gap-1.5"><span style={{ color: '#33d670' }}>✓</span> Aufsetz-Service kostenlos (sonst 990 € Pauschale)</li>
                      <li className="flex gap-1.5"><span style={{ color: '#33d670' }}>✓</span> Testing-Service während der Beta kostenlos</li>
                      <li className="flex gap-1.5"><span style={{ color: '#33d670' }}>✓</span> 100 % Remote-Support direkt vom Dev-Team</li>
                      <li className="flex gap-1.5"><span style={{ color: '#33d670' }}>✓</span> Mitsprache an der Feature-Roadmap</li>
                    </ul>
                    <div className="text-[10px] text-app-text-mute mt-2 leading-snug">
                      Genaue Anzahlungs-Höhe wird im individuellen Angebot festgelegt — Bestätigung hier ist unverbindlich.
                    </div>
                  </div>
                </label>
              </Field>

              <Field label="Was sollten wir noch wissen? (optional)">
                <textarea
                  rows={2}
                  value={form.notes}
                  onChange={e => setForm({ ...form, notes: e.target.value })}
                  placeholder="z. B. Sonderwünsche, IT-Setup, Migrations-Bedarf …"
                  className="beta-input"
                />
              </Field>

              {error && (
                <div className="mt-2 p-3 rounded-lg text-xs" style={{
                  background: 'rgba(255,80,80,0.12)',
                  border: '1px solid rgba(255,80,80,0.35)',
                  color: '#ff8a8a',
                }}>
                  {error}
                </div>
              )}

              <div className="mt-4 text-[10px] text-app-text-mute leading-relaxed">
                Mit dem Absenden willigst du ein, dass wir deine Angaben zur Bearbeitung speichern und uns per E-Mail melden.
                Keine Weitergabe an Dritte. <a href="https://custosoftcustomers.com/datenschutz" className="underline hover:text-app-text">Datenschutz</a>.
              </div>
            </div>

            {/* Sticky Footer mit Submit-Button — ist immer sichtbar */}
            <div
              className="px-5 sm:px-6 py-3 flex flex-col-reverse sm:flex-row gap-2 shrink-0"
              style={{ borderTop: '1px solid rgba(255,255,255,0.08)', background: 'rgba(20,15,30,0.50)' }}
            >
              <button
                type="button"
                onClick={onClose}
                className="px-4 py-2.5 rounded-full text-sm font-semibold border-2 border-app-border hover:bg-app-elev-2/40 transition"
              >
                Abbrechen
              </button>
              <button
                type="submit"
                disabled={submitting}
                className="flex-1 px-5 py-2.5 rounded-full font-semibold text-white text-sm disabled:opacity-60"
                style={{ background: 'linear-gradient(135deg, #ffaa3d, #f2404c, #8a44ee)' }}
              >
                {submitting ? 'Wird gesendet…' : '🚀 Beta-Slot reservieren'}
              </button>
            </div>
          </form>
        )}
      </div>

      {/* Inline-Style für Form-Inputs — einmal pro Modal */}
      <style>{`
        .beta-input {
          width: 100%;
          padding: 9px 12px;
          background: rgba(255,255,255,0.05);
          border: 1px solid rgba(255,255,255,0.10);
          border-radius: 10px;
          color: #fff;
          font-size: 13.5px;
          font-family: inherit;
          outline: none;
          transition: all 0.15s;
          resize: vertical;
        }
        .beta-input:focus {
          background: rgba(255,255,255,0.08);
          border-color: rgba(138,68,238,0.55);
          box-shadow: 0 0 0 3px rgba(138,68,238,0.20);
        }
        .beta-input::placeholder { color: rgba(255,255,255,0.30); }
      `}</style>
    </div>
  )
}

function Field({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <div className="mb-3">
      <div className="text-[11px] font-semibold text-app-text-dim mb-1 uppercase tracking-wider">{label}</div>
      {children}
    </div>
  )
}

function RadioPills({
  options, value, onChange,
}: {
  options: { value: string; label: string }[]
  value:   string
  onChange: (v: string) => void
}) {
  return (
    <div className="flex flex-wrap gap-2">
      {options.map(o => {
        const active = value === o.value
        return (
          <button
            type="button"
            key={o.value}
            onClick={() => onChange(active ? '' : o.value)}
            className="px-3.5 py-2 rounded-full text-xs font-semibold transition"
            style={{
              background: active ? 'linear-gradient(135deg, #8a44ee, #3a62ff)' : 'rgba(255,255,255,0.04)',
              border: '1px solid ' + (active ? 'rgba(138,68,238,0.60)' : 'rgba(255,255,255,0.10)'),
              color: active ? '#fff' : 'rgba(255,255,255,0.70)',
            }}
          >
            {o.label}
          </button>
        )
      })}
    </div>
  )
}

// ════════════════════════════════════════════════════════════════════════════
// 12. FOOTER
// ════════════════════════════════════════════════════════════════════════════
function ServerFooter() {
  return (
    <footer className="relative py-10 px-6 border-t" style={{ borderColor: 'var(--border)' }}>
      <div className="max-w-6xl mx-auto flex flex-wrap justify-between items-center gap-4 text-xs text-app-text-mute">
        <div>© 2026 Taironic Media · CustoSoft Server</div>
        <div className="flex gap-5">
          <Link to="/" className="hover:text-app-text">SaaS-Variante</Link>
          <a href="https://custosoftcustomers.com/agb" className="hover:text-app-text">AGB</a>
          <a href="https://custosoftcustomers.com/datenschutz" className="hover:text-app-text">Datenschutz</a>
          <a href="https://custosoftcustomers.com/impressum" className="hover:text-app-text">Impressum</a>
        </div>
      </div>
    </footer>
  )
}

// ════════════════════════════════════════════════════════════════════════════
// PARTICLE FIELD — Schwebende Lichtpunkte im Hero
// ════════════════════════════════════════════════════════════════════════════
function ParticleField({ count = 30 }: { count?: number }) {
  const ref = useRef<HTMLDivElement>(null)
  useEffect(() => {
    if (!ref.current) return
    const container = ref.current
    container.innerHTML = ''
    for (let i = 0; i < count; i++) {
      const p = document.createElement('div')
      p.className = 'particle'
      p.style.left = (Math.random() * 100) + '%'
      p.style.top  = (100 + Math.random() * 20) + '%'
      p.style.animationDuration = (12 + Math.random() * 16) + 's'
      p.style.animationDelay    = (-Math.random() * 24) + 's'
      p.style.opacity           = String(0.3 + Math.random() * 0.5)
      p.style.transform         = `scale(${0.5 + Math.random() * 1.2})`
      container.appendChild(p)
    }
  }, [count])
  return <div ref={ref} className="absolute inset-0 pointer-events-none overflow-hidden" />
}
